import { ELEVEN_LABS_API_KEY, ELEVEN_LABS_VOICE_ID } from 'config/app.config';
import audioManager from './AudioManager';
import {
  fetchLessonAudioManifest,
  fetchLessonAudioRecord,
} from './LessonContentService';

const TTS_ENDPOINT = 'https://api.elevenlabs.io/v1/text-to-speech';
const TUTOR_AUDIO_CHANNEL = 'tutor-speech';
const TUTOR_CHAT_AUDIO_CHANNEL = 'tutor-chat-speech';

// Per FRONTEND_LESSON_AUDIO_FLOW.md, presigned manifest URLs have a finite
// TTL. Refresh the manifest a little before they expire so that playback
// never hits a 403 mid-clip; if it does, we fall back to a reactive refresh.
const MANIFEST_REFRESH_LEAD_MS = 60 * 1000;
const MANIFEST_REFRESH_MIN_MS = 5 * 1000;

// Hard-coded UI prompts (tutorials, knowledge-check summaries, etc.) are
// never pre-rendered, so we skip the per-clip download attempt for them
// and go straight to live TTS. Backend-authored audio still tries the
// manifest + download path first and only falls through to live TTS when
// both fail.
const SYSTEM_AUDIO_PREFIX = 'system.';
function isSystemAudioId(audioId) {
  return typeof audioId === 'string' && audioId.startsWith(SYSTEM_AUDIO_PREFIX);
}

// Tutor chat outranks lesson narration: opening / replying in the chat
// preempts narration (paused so its position survives), and any narration
// request fired while a chat reply is mid-air is rejected by AudioManager.
audioManager.defineChannel(TUTOR_AUDIO_CHANNEL, {
  priority: 1,
  suspendable: true,
});
audioManager.defineChannel(TUTOR_CHAT_AUDIO_CHANNEL, {
  priority: 2,
  suspendable: false,
});

const MIN_TUTOR_PLAYBACK_RATE = 0.5;
const MAX_TUTOR_PLAYBACK_RATE = 1.5;
const MIN_TUTOR_VOLUME = 0;
const MAX_TUTOR_VOLUME = 1;
let tutorPlaybackRate = 1;
let tutorVolume = 1;

let activeLessonScope = null;
const audioCache = new Map();
const pendingGenerations = new Map();
const queuedPrefetchKeys = new Set();
const prefetchQueue = [];
let prefetchRunning = false;

/**
 * Registry of pre-rendered audio manifests, keyed by `lessonScope`
 * (`lesson:<lessonId>`). Each registration holds the credentials and
 * lesson-content id needed to (a) refresh the manifest before its
 * presigned URLs expire, and (b) hit the per-clip download endpoint as
 * a per-clip fallback when the manifest is missing an entry.
 */
const manifestRegistry = new Map();

function clearRegistration(registration) {
  if (!registration) return;
  if (registration.refreshTimer) {
    clearTimeout(registration.refreshTimer);
    registration.refreshTimer = null;
  }
}

function indexManifestClips(manifest) {
  const clipsArray = Array.isArray(manifest?.clips) ? manifest.clips : [];
  const clipsByAudioId = new Map();
  let earliestExpiry = null;
  for (const clip of clipsArray) {
    if (!clip?.audioId) continue;
    clipsByAudioId.set(clip.audioId, clip);
    const expiresMs = clip.urlExpiresAt
      ? Date.parse(clip.urlExpiresAt)
      : null;
    if (Number.isFinite(expiresMs)) {
      if (earliestExpiry == null || expiresMs < earliestExpiry) {
        earliestExpiry = expiresMs;
      }
    }
  }
  return {clipsByAudioId, earliestExpiry};
}

function scheduleManifestRefresh(lessonScope, registration) {
  if (!registration) return;
  clearRegistration(registration);
  if (registration.earliestExpiry == null) return;
  const now = Date.now();
  const delay = Math.max(
    MANIFEST_REFRESH_MIN_MS,
    registration.earliestExpiry - now - MANIFEST_REFRESH_LEAD_MS,
  );
  registration.refreshTimer = setTimeout(() => {
    refreshManifest(lessonScope).catch(error => {
      // Refresh failures are non-fatal; the next playback will reactively
      // refresh on the first 403 instead.
      console.warn(
        '[TextToSpeechService] manifest refresh failed:',
        error?.message || error,
      );
    });
  }, delay);
}

async function refreshManifest(lessonScope) {
  const registration = manifestRegistry.get(lessonScope);
  if (!registration?.lessonContentId || !registration?.token) return null;
  if (registration.refreshPromise) return registration.refreshPromise;

  const fetchPromise = fetchLessonAudioManifest(
    registration.lessonContentId,
    registration.token,
  )
    .then(manifest => {
      registration.refreshPromise = null;
      if (!manifest) {
        // Manifest disappeared (e.g. backend wiped); keep stale data so we
        // still know the lesson scope but force per-clip fallbacks.
        registration.manifest = null;
        registration.clipsByAudioId = new Map();
        registration.earliestExpiry = null;
        return null;
      }
      const {clipsByAudioId, earliestExpiry} = indexManifestClips(manifest);
      registration.manifest = manifest;
      registration.clipsByAudioId = clipsByAudioId;
      registration.earliestExpiry = earliestExpiry;
      // Drop cached entries whose URLs may have rotated so subsequent plays
      // pick up the new presigned URL + timing metadata.
      clearCacheEntries((key, entry) => {
        if (!key.startsWith(`${lessonScope}::`)) return false;
        return entry.source === 'manifest' || entry.source === 'download';
      });
      scheduleManifestRefresh(lessonScope, registration);
      return manifest;
    })
    .catch(error => {
      registration.refreshPromise = null;
      throw error;
    });

  registration.refreshPromise = fetchPromise;
  return fetchPromise;
}

/**
 * Register a pre-rendered audio manifest for a lesson. Subsequent calls
 * to `playTutorAudio({lessonScope, audioId, ...})` will look up the clip
 * by id, prefer the manifest's presigned URL + timing metadata, and only
 * fall back to per-clip download or live ElevenLabs when needed.
 */
export function registerLessonAudioManifest({
  lessonScope,
  lessonContentId,
  manifest,
  token,
}) {
  if (!lessonScope) return;

  const previous = manifestRegistry.get(lessonScope);
  clearRegistration(previous);

  const {clipsByAudioId, earliestExpiry} = indexManifestClips(manifest);
  const registration = {
    lessonContentId: lessonContentId || previous?.lessonContentId || null,
    token: token || previous?.token || null,
    manifest: manifest || null,
    clipsByAudioId,
    earliestExpiry,
    refreshTimer: null,
    refreshPromise: null,
  };
  manifestRegistry.set(lessonScope, registration);
  scheduleManifestRefresh(lessonScope, registration);
}

/**
 * Forget a previously registered manifest. Called when leaving the
 * lesson viewer so we don't keep stale credentials or schedule wasted
 * refresh timers.
 */
export function clearLessonAudioManifest(lessonScope) {
  if (!lessonScope) {
    for (const registration of manifestRegistry.values()) {
      clearRegistration(registration);
    }
    manifestRegistry.clear();
    return;
  }
  const registration = manifestRegistry.get(lessonScope);
  clearRegistration(registration);
  manifestRegistry.delete(lessonScope);
}

function getManifestClip(lessonScope, audioId) {
  const registration = manifestRegistry.get(lessonScope);
  if (!registration?.clipsByAudioId) return null;
  return registration.clipsByAudioId.get(audioId) || null;
}

function isClipUrlExpired(clip) {
  if (!clip?.urlExpiresAt) return false;
  const expiresMs = Date.parse(clip.urlExpiresAt);
  if (!Number.isFinite(expiresMs)) return false;
  return expiresMs - Date.now() <= MANIFEST_REFRESH_MIN_MS;
}

function revokeEntry(entry) {
  if (entry?.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
  }
}

function getCacheKey(lessonScope, audioId) {
  return `${lessonScope || 'global'}::${audioId}`;
}

function setActiveLessonScope(lessonScope) {
  if (lessonScope) {
    activeLessonScope = lessonScope;
  }
}

function stopCurrentPlayback(triggerStop = true, invalidate = true) {
  audioManager.stop(TUTOR_AUDIO_CHANNEL, { triggerStop, invalidate });
}

function normalizePlaybackRate(rate) {
  if (!Number.isFinite(rate)) return 1;
  const roundedRate = Math.round(rate * 10) / 10;
  return Math.min(MAX_TUTOR_PLAYBACK_RATE, Math.max(MIN_TUTOR_PLAYBACK_RATE, roundedRate));
}

function normalizeVolumePercent(volumePercent) {
  if (!Number.isFinite(volumePercent)) return 100;
  return Math.min(100, Math.max(0, Math.round(volumePercent)));
}

function clearCacheEntries(predicate) {
  Array.from(audioCache.entries()).forEach(([key, entry]) => {
    if (!predicate(key, entry)) return;
    revokeEntry(entry);
    audioCache.delete(key);
  });
}

async function requestSpeechBlob(text) {
  if (!text) {
    throw new Error('Cannot generate speech without text');
  }
  if (!ELEVEN_LABS_API_KEY) {
    throw new Error('ELEVEN_LABS_API_KEY is not set');
  }
  if (!ELEVEN_LABS_VOICE_ID) {
    throw new Error('ELEVEN_LABS_VOICE_ID is not set');
  }

  const url = `${TTS_ENDPOINT}/${ELEVEN_LABS_VOICE_ID}`;

  const response = await fetch(
    url,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'xi-api-key': ELEVEN_LABS_API_KEY,
      },
      body: JSON.stringify({
        text,
        model_id: 'eleven_monolingual_v1',
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.5,
        },
      }),
    },
  );

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '(could not read body)');
    throw new Error(`ElevenLabs API error: ${response.status} ${errorBody}`);
  }

  return response.blob();
}

function clipToEntry({lessonScope, audioId, text, clip, source}) {
  return {
    audioId,
    text,
    lessonScope,
    source,
    srcUrl: clip.url || null,
    urlExpiresAt: clip.urlExpiresAt || null,
    durationMs: Number.isFinite(clip.durationMs) ? clip.durationMs : null,
    mimeType: clip.mimeType || null,
    timingMetadata: clip.timingMetadata || null,
  };
}

async function buildEntryFromManifest({lessonScope, audioId, text}) {
  let clip = getManifestClip(lessonScope, audioId);
  if (clip && isClipUrlExpired(clip)) {
    try {
      await refreshManifest(lessonScope);
      clip = getManifestClip(lessonScope, audioId);
    } catch {
      // Reactive refresh failed; let downstream fallbacks handle it.
    }
  }
  if (!clip?.url) return null;
  return clipToEntry({
    lessonScope,
    audioId,
    text,
    clip,
    source: 'manifest',
  });
}

async function buildEntryFromDownload({lessonScope, audioId, text}) {
  const registration = manifestRegistry.get(lessonScope);
  if (!registration?.lessonContentId || !registration?.token) return null;
  const record = await fetchLessonAudioRecord(
    audioId,
    registration.lessonContentId,
    registration.token,
    {autoGenerate: true},
  );
  if (!record?.url) return null;
  // Surface the freshly minted clip to the manifest registry so that
  // future plays of this audio id can hit the manifest path directly.
  if (registration.clipsByAudioId) {
    registration.clipsByAudioId.set(audioId, record);
  }
  return clipToEntry({
    lessonScope,
    audioId,
    text,
    clip: record,
    source: 'download',
  });
}

async function buildEntryFromTts({lessonScope, audioId, text}) {
  if (!text) {
    throw new Error(`Cannot synthesize empty text for audioId=${audioId}`);
  }
  const audioBlob = await requestSpeechBlob(text);
  const objectUrl = URL.createObjectURL(audioBlob);
  return {
    audioId,
    text,
    lessonScope,
    source: 'tts',
    objectUrl,
    srcUrl: objectUrl,
    urlExpiresAt: null,
    durationMs: null,
    mimeType: audioBlob?.type || null,
    timingMetadata: null,
  };
}

async function ensureAudioEntry({ lessonScope, audioId, text }) {
  setActiveLessonScope(lessonScope);
  const cacheKey = getCacheKey(lessonScope, audioId);
  const cachedEntry = audioCache.get(cacheKey);
  if (cachedEntry) {
    return cachedEntry;
  }

  const pending = pendingGenerations.get(cacheKey);
  if (pending) {
    return pending;
  }

  const generationPromise = (async () => {
    let entry = null;

    // System.* prompts are never pre-rendered, so skip the backend
    // lookups entirely and go straight to live TTS.
    if (!isSystemAudioId(audioId)) {
      entry = await buildEntryFromManifest({lessonScope, audioId, text});

      if (!entry) {
        try {
          entry = await buildEntryFromDownload({lessonScope, audioId, text});
        } catch (error) {
          // Per-clip download failures (400/403/500/etc.) are non-fatal:
          // log and fall through to the live-TTS fallback below so a
          // transient backend hiccup never silently kills playback. The
          // recovered clip will sound slightly different from the
          // pre-rendered voice, but that's better than no audio.
          console.warn(
            '[TextToSpeechService] per-clip download failed for',
            audioId,
            '— falling back to live TTS:',
            error?.message || error,
          );
        }
      }
    }

    if (!entry && text) {
      try {
        entry = await buildEntryFromTts({lessonScope, audioId, text});
      } catch (ttsError) {
        // We've now exhausted manifest, per-clip download, and live TTS.
        // Surface the TTS error so the caller knows nothing played.
        console.warn(
          '[TextToSpeechService] live TTS fallback failed for',
          audioId,
          ttsError?.message || ttsError,
        );
        throw ttsError;
      }
    }

    if (!entry) {
      throw new Error(
        `No audio available for ${audioId}: no pre-rendered clip and no text was provided for the live-TTS fallback`,
      );
    }

    if (
      lessonScope &&
      activeLessonScope &&
      lessonScope !== activeLessonScope
    ) {
      revokeEntry(entry);
      return entry;
    }

    audioCache.set(cacheKey, entry);
    return entry;
  })()
    .catch(error => {
      pendingGenerations.delete(cacheKey);
      throw error;
    })
    .then(entry => {
      pendingGenerations.delete(cacheKey);
      return entry;
    });

  pendingGenerations.set(cacheKey, generationPromise);
  return generationPromise;
}

async function drainPrefetchQueue() {
  if (prefetchRunning) return;
  prefetchRunning = true;

  while (prefetchQueue.length) {
    const nextTask = prefetchQueue.shift();
    if (!nextTask) continue;
    queuedPrefetchKeys.delete(nextTask.cacheKey);

    if (
      activeLessonScope &&
      nextTask.lessonScope &&
      nextTask.lessonScope !== activeLessonScope
    ) {
      continue;
    }

    try {
      await ensureAudioEntry(nextTask);
    } catch {
      // Prefetch failures should not interrupt active playback.
    }
  }

  prefetchRunning = false;
}

/**
 * Stop any in-flight TTS audio so voices don't overlap when the tutor
 * text changes rapidly (e.g. quick phase transitions).
 */
export function stopTutorSpeech() {
  stopCurrentPlayback(true, true);
}

export function pauseTutorSpeech() {
  return audioManager.pause(TUTOR_AUDIO_CHANNEL);
}

export async function resumeTutorSpeech() {
  return audioManager.resume(TUTOR_AUDIO_CHANNEL);
}

export function getTutorSpeechPlaybackRate() {
  return tutorPlaybackRate;
}

export function setTutorSpeechPlaybackRate(nextRate) {
  const normalizedRate = normalizePlaybackRate(nextRate);
  tutorPlaybackRate = normalizedRate;
  const activeAudio = audioManager.getCurrentAudio(TUTOR_AUDIO_CHANNEL);
  if (activeAudio) {
    activeAudio.playbackRate = normalizedRate;
  }
  return normalizedRate;
}

export function getTutorSpeechVolume() {
  return Math.round(tutorVolume * 100);
}

export function setTutorSpeechVolume(nextVolumePercent) {
  const normalizedPercent = normalizeVolumePercent(nextVolumePercent);
  tutorVolume = Math.min(
    MAX_TUTOR_VOLUME,
    Math.max(MIN_TUTOR_VOLUME, normalizedPercent / 100),
  );
  const activeAudio = audioManager.getCurrentAudio(TUTOR_AUDIO_CHANNEL);
  if (activeAudio) {
    activeAudio.volume = tutorVolume;
  }
  return normalizedPercent;
}

/**
 * Revoke any cached object URLs associated with the provided lesson scope.
 */
export function clearTutorAudioCache(lessonScope) {
  if (!lessonScope) {
    stopCurrentPlayback(false, true);
    clearCacheEntries(() => true);
    prefetchQueue.length = 0;
    queuedPrefetchKeys.clear();
    activeLessonScope = null;
    clearLessonAudioManifest();
    return;
  }

  const currentAudioKey = audioManager.getCurrentAudioKey(TUTOR_AUDIO_CHANNEL);
  if (currentAudioKey && currentAudioKey.startsWith(`${lessonScope}::`)) {
    stopCurrentPlayback(false, true);
  }
  clearCacheEntries(key => key.startsWith(`${lessonScope}::`));
  for (let index = prefetchQueue.length - 1; index >= 0; index -= 1) {
    const queued = prefetchQueue[index];
    if (queued.lessonScope !== lessonScope) continue;
    queuedPrefetchKeys.delete(queued.cacheKey);
    prefetchQueue.splice(index, 1);
  }
  if (activeLessonScope === lessonScope) {
    activeLessonScope = null;
  }
  clearLessonAudioManifest(lessonScope);
}

/**
 * Ensure tutor audio is generated and cached.
 */
export async function ensureTutorAudio({
  lessonScope,
  audioId,
  text,
}) {
  return ensureAudioEntry({lessonScope, audioId, text});
}

/**
 * Queue future tutor audio clips for sequential prefetch.
 */
export function queueTutorAudioPrefetch({lessonScope, entries = []}) {
  setActiveLessonScope(lessonScope);
  const activeAudioKey = audioManager.getCurrentAudioKey(TUTOR_AUDIO_CHANNEL);

  entries
    .filter(entry => entry?.audioId && entry?.text)
    .forEach(entry => {
      const cacheKey = getCacheKey(lessonScope, entry.audioId);
      if (
        audioCache.has(cacheKey) ||
        pendingGenerations.has(cacheKey) ||
        queuedPrefetchKeys.has(cacheKey) ||
        activeAudioKey === cacheKey
      ) {
        return;
      }

      queuedPrefetchKeys.add(cacheKey);
      prefetchQueue.push({
        lessonScope,
        audioId: entry.audioId,
        text: entry.text,
        cacheKey,
      });
    });

  void drainPrefetchQueue();
}

/**
 * Play tutor audio from cache, generating and caching it on demand if needed.
 */
export async function playTutorAudio({
  lessonScope,
  audioId,
  text,
  onPlaybackStart,
  onPlaybackStop,
  onPlaybackProgress,
}) {
  if (!text || !audioId) {
    return;
  }

  const requestToken = audioManager.beginRequest(TUTOR_AUDIO_CHANNEL);
  // AudioManager rejects narration while a higher-priority channel
  // (e.g. tutor chat) is mid-playback. Bail without firing callbacks.
  if (requestToken == null) {
    return;
  }

  setActiveLessonScope(lessonScope);
  const entry = await ensureAudioEntry({ lessonScope, audioId, text });
  if (!audioManager.isRequestActive(TUTOR_AUDIO_CHANNEL, requestToken)) {
    return;
  }

  const cacheKey = getCacheKey(lessonScope, audioId);
  const audio = new Audio(entry.srcUrl || entry.objectUrl);
  audio.playbackRate = tutorPlaybackRate;
  audio.volume = tutorVolume;

  return new Promise((resolve, reject) => {
    let settled = false;
    let progressTimer = null;
    let retriedExpiredUrl = false;

    const getDurationMs = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        return audio.duration * 1000;
      }
      // Manifest-provided duration is reliable even before the
      // <audio> element has finished probing the source.
      return Number.isFinite(entry.durationMs) ? entry.durationMs : null;
    };

    const stopProgressUpdates = () => {
      if (progressTimer) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
    };

    const emitProgress = () => {
      onPlaybackProgress?.({
        audioId,
        currentTimeMs: Math.max(0, audio.currentTime * 1000),
        durationMs: getDurationMs(),
      });
    };

    const startProgressUpdates = () => {
      emitProgress();
      stopProgressUpdates();
      progressTimer = setInterval(emitProgress, 120);
    };

    const finalize = callback => {
      if (settled) return;
      settled = true;
      stopProgressUpdates();
      audioManager.clearPlaybackIfMatches(TUTOR_AUDIO_CHANNEL, audio);
      callback?.();
      resolve();
    };

    const finishPlayback = () => {
      if (settled) return;
      settled = true;
      stopProgressUpdates();
      resolve();
    };

    const didRegister = audioManager.registerPlayback(TUTOR_AUDIO_CHANNEL, requestToken, {
      audio,
      audioKey: cacheKey,
      onStop: onPlaybackStop || null,
      onFinish: finishPlayback,
      startProgressUpdates,
      stopProgressUpdates,
    });

    if (!didRegister) {
      resolve();
      return;
    }

    audio.onended = () => {
      emitProgress();
      finalize(onPlaybackStop);
    };

    // Presigned manifest URLs can 403 mid-load if S3 rotates them, and
    // the per-clip download endpoint can transiently 4xx/5xx. Try a
    // single reactive recovery: drop the cached entry, refresh the
    // manifest, and replay through the standard resolution chain. If
    // that still fails, fall back to live TTS so playback never silently
    // dies on a backend hiccup.
    const handleAudioError = async err => {
      if (settled) return;
      const fromManifestOrDownload =
        entry.source === 'manifest' || entry.source === 'download';
      if (fromManifestOrDownload && !retriedExpiredUrl) {
        retriedExpiredUrl = true;
        stopProgressUpdates();
        audioManager.clearPlaybackIfMatches(TUTOR_AUDIO_CHANNEL, audio);
        audioCache.delete(cacheKey);
        try {
          await refreshManifest(lessonScope);
          await playTutorAudio({
            lessonScope,
            audioId,
            text,
            onPlaybackStart,
            onPlaybackStop,
            onPlaybackProgress,
          });
          settled = true;
          resolve();
          return;
        } catch (refreshError) {
          console.warn(
            '[TextToSpeechService] manifest-refresh replay failed for',
            audioId,
            '— attempting live TTS fallback:',
            refreshError?.message || refreshError,
          );
          if (text) {
            try {
              const ttsEntry = await buildEntryFromTts({
                lessonScope,
                audioId,
                text,
              });
              audioCache.set(cacheKey, ttsEntry);
              await playTutorAudio({
                lessonScope,
                audioId,
                text,
                onPlaybackStart,
                onPlaybackStop,
                onPlaybackProgress,
              });
              settled = true;
              resolve();
              return;
            } catch (ttsError) {
              settled = true;
              onPlaybackStop?.();
              reject(ttsError);
              return;
            }
          }
          settled = true;
          onPlaybackStop?.();
          reject(refreshError);
          return;
        }
      }
      settled = true;
      stopProgressUpdates();
      audioManager.clearPlaybackIfMatches(TUTOR_AUDIO_CHANNEL, audio);
      onPlaybackStop?.();
      reject(err);
    };
    audio.onerror = handleAudioError;

    audio.play()
      .then(() => {
        if (!audioManager.isRequestActive(TUTOR_AUDIO_CHANNEL, requestToken)) {
          finalize(onPlaybackStop);
          return;
        }

        onPlaybackStart?.({
          audioId,
          durationMs: getDurationMs(),
          wordTimings: entry.timingMetadata?.words || null,
          source: entry.source,
        });
        startProgressUpdates();
      })
      .catch(err => {
        handleAudioError(err);
      });
  });
}

/**
 * Play one-shot tutor chat reply audio on a dedicated channel so it does
 * not clobber the lesson narration channel (which may be paused mid-clip
 * while the chat panel is open). Replies are not cached because each
 * tutor response is unique to the conversation.
 */
export async function playTutorChatSpeech({
  text,
  onPlaybackStart,
  onPlaybackStop,
  onPlaybackProgress,
}) {
  if (!text) return;

  const requestToken = audioManager.beginRequest(TUTOR_CHAT_AUDIO_CHANNEL);
  // Defensive: chat is the highest-priority channel today, but if a future
  // channel ever outranks it, AudioManager will say so and we bow out.
  if (requestToken == null) return;
  const audioBlob = await requestSpeechBlob(text);
  if (!audioManager.isRequestActive(TUTOR_CHAT_AUDIO_CHANNEL, requestToken)) {
    return;
  }

  const objectUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(objectUrl);
  audio.playbackRate = tutorPlaybackRate;
  audio.volume = tutorVolume;

  return new Promise((resolve, reject) => {
    let settled = false;
    let progressTimer = null;

    const getDurationMs = () =>
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration * 1000
        : null;

    const stopProgressUpdates = () => {
      if (progressTimer) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
    };

    const emitProgress = () => {
      onPlaybackProgress?.({
        currentTimeMs: Math.max(0, audio.currentTime * 1000),
        durationMs: getDurationMs(),
      });
    };

    const startProgressUpdates = () => {
      emitProgress();
      stopProgressUpdates();
      progressTimer = setInterval(emitProgress, 120);
    };

    const releaseObjectUrl = () => {
      URL.revokeObjectURL(objectUrl);
    };

    const finalize = callback => {
      if (settled) return;
      settled = true;
      stopProgressUpdates();
      audioManager.clearPlaybackIfMatches(TUTOR_CHAT_AUDIO_CHANNEL, audio);
      releaseObjectUrl();
      callback?.();
      resolve();
    };

    const finishPlayback = () => {
      if (settled) return;
      settled = true;
      stopProgressUpdates();
      releaseObjectUrl();
      resolve();
    };

    const didRegister = audioManager.registerPlayback(
      TUTOR_CHAT_AUDIO_CHANNEL,
      requestToken,
      {
        audio,
        audioKey: `chat::${Date.now()}`,
        onStop: onPlaybackStop || null,
        onFinish: finishPlayback,
        startProgressUpdates,
        stopProgressUpdates,
      },
    );

    if (!didRegister) {
      releaseObjectUrl();
      resolve();
      return;
    }

    audio.onended = () => {
      emitProgress();
      finalize(onPlaybackStop);
    };
    audio.onerror = err => {
      stopProgressUpdates();
      audioManager.clearPlaybackIfMatches(TUTOR_CHAT_AUDIO_CHANNEL, audio);
      releaseObjectUrl();
      onPlaybackStop?.();
      reject(err);
    };

    audio
      .play()
      .then(() => {
        if (!audioManager.isRequestActive(TUTOR_CHAT_AUDIO_CHANNEL, requestToken)) {
          finalize(onPlaybackStop);
          return;
        }

        onPlaybackStart?.({
          durationMs: getDurationMs(),
        });
        startProgressUpdates();
      })
      .catch(err => {
        stopProgressUpdates();
        audioManager.clearPlaybackIfMatches(TUTOR_CHAT_AUDIO_CHANNEL, audio);
        releaseObjectUrl();
        onPlaybackStop?.();
        reject(err);
      });
  });
}

/**
 * Stop any in-flight tutor chat reply playback.
 */
export function stopTutorChatSpeech() {
  audioManager.stop(TUTOR_CHAT_AUDIO_CHANNEL, {triggerStop: true, invalidate: true});
}

/**
 * Backwards-compatible helper for text-only playback.
 */
export async function speakTutorText(text) {
  if (!text) {
    stopTutorSpeech();
    return;
  }

  return playTutorAudio({
    lessonScope: 'global',
    audioId: `text:${text}`,
    text,
  });
}
